import React, { useState, useEffect } from 'react';
import { Button, Input, Textarea, Select, SelectItem, Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, useDisclosure, Spinner } from '@nextui-org/react';
import { FiPlus, FiEdit2, FiTrash2, FiAlertTriangle, FiClock, FiCalendar, FiGlobe, FiServer, FiDatabase, FiShield, FiMail, FiCheck } from 'react-icons/fi';
import { format, addDays, differenceInDays, isAfter, isBefore, startOfDay } from 'date-fns';
import { tr } from 'date-fns/locale';
import { 
  getReminders, 
  createReminder, 
  updateReminder, 
  deleteReminder, 
  completeReminder,
  REMINDER_TYPES,
  REMINDER_STATUS,
  getReminderTypeLabel,
  getReminderStatusLabel,
  getReminderStatusColor,
  getReminderTypeColor
} from '../../../services/reminder';

const RemindersComponent = ({ projectId }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [reminders, setReminders] = useState([]);
  const [editingReminder, setEditingReminder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [newReminder, setNewReminder] = useState({
    name: '',
    description: '',
    type: 'domain',
    endDate: '',
    reminderDays: 30
  });

  // API'den hatırlatıcıları yükle
  useEffect(() => {
    const fetchReminders = async () => {
      if (!projectId) return;
      
      setLoading(true);
      setError('');
      try {
        const response = await getReminders(projectId);
        setReminders(response.reminders);
      } catch (err) {
        setError('Hatırlatıcılar yüklenirken bir hata oluştu.');
        setReminders([]);
      } finally {
        setLoading(false);
      }
    };

    fetchReminders();
  }, [projectId]);

  const reminderTypes = [
    { key: REMINDER_TYPES.DOMAIN, label: getReminderTypeLabel(REMINDER_TYPES.DOMAIN), icon: FiGlobe, color: 'text-blue-500' },
    { key: REMINDER_TYPES.HOSTING, label: getReminderTypeLabel(REMINDER_TYPES.HOSTING), icon: FiServer, color: 'text-green-500' },
    { key: REMINDER_TYPES.SSL, label: getReminderTypeLabel(REMINDER_TYPES.SSL), icon: FiShield, color: 'text-purple-500' },
    { key: REMINDER_TYPES.BACKUP, label: getReminderTypeLabel(REMINDER_TYPES.BACKUP), icon: FiDatabase, color: 'text-orange-500' },
    { key: REMINDER_TYPES.OTHER, label: getReminderTypeLabel(REMINDER_TYPES.OTHER), icon: FiClock, color: 'text-gray-500' }
  ];

  const getReminderType = (type) => {
    return reminderTypes.find(t => t.key === type) || reminderTypes[reminderTypes.length - 1];
  };

  const getStatusInfo = (reminder) => {
    const today = startOfDay(new Date());
    const expiry = startOfDay(new Date(reminder.endDate));
    const daysUntilExpiry = differenceInDays(expiry, today);
    const reminderDate = addDays(expiry, -reminder.reminderDays);

    // API'den gelen status'u kontrol et
    if (reminder.status === REMINDER_STATUS.EXPIRED) {
      return {
        status: 'expired',
        text: getReminderStatusLabel(REMINDER_STATUS.EXPIRED),
        color: 'text-red-600 dark:text-red-400',
        bgColor: 'bg-red-50 dark:bg-red-900/20',
        borderColor: 'border-red-200 dark:border-red-800'
      };
    } else if (reminder.status === REMINDER_STATUS.COMPLETED) {
      return {
        status: 'completed',
        text: getReminderStatusLabel(REMINDER_STATUS.COMPLETED),
        color: 'text-blue-600 dark:text-blue-400',
        bgColor: 'bg-blue-50 dark:bg-blue-900/20',
        borderColor: 'border-blue-200 dark:border-blue-800'
      };
    } else if (isBefore(expiry, today)) {
      return {
        status: 'expired',
        text: 'Süresi Dolmuş',
        color: 'text-red-600 dark:text-red-400',
        bgColor: 'bg-red-50 dark:bg-red-900/20',
        borderColor: 'border-red-200 dark:border-red-800'
      };
    } else if (isBefore(reminderDate, today)) {
      return {
        status: 'warning',
        text: `${daysUntilExpiry} gün kaldı`,
        color: 'text-orange-600 dark:text-orange-400',
        bgColor: 'bg-orange-50 dark:bg-orange-900/20',
        borderColor: 'border-orange-200 dark:border-orange-800'
      };
    } else {
      return {
        status: 'safe',
        text: `${daysUntilExpiry} gün kaldı`,
        color: 'text-green-600 dark:text-green-400',
        bgColor: 'bg-green-50 dark:bg-green-900/20',
        borderColor: 'border-green-200 dark:border-green-800'
      };
    }
  };

  const handleAddReminder = () => {
    setEditingReminder(null);
    setNewReminder({
      name: '',
      description: '',
      type: REMINDER_TYPES.DOMAIN,
      endDate: '',
      reminderDays: 30
    });
    onOpen();
  };

  const handleEditReminder = (reminder) => {
    setEditingReminder(reminder);
    setNewReminder({
      name: reminder.name,
      description: reminder.description,
      type: reminder.type,
      endDate: reminder.endDate,
      reminderDays: reminder.reminderDays
    });
    onOpen();
  };

  const handleSaveReminder = async () => {
    setLoading(true);
    setError('');
    
    try {
      if (editingReminder) {
        // Güncelleme
        const response = await updateReminder(editingReminder.id, newReminder);
        setReminders(prev => 
          prev.map(r => r.id === editingReminder.id ? response.reminder : r)
        );
      } else {
        // Yeni ekleme
        const response = await createReminder(projectId, newReminder);
        setReminders(prev => [...prev, response.reminder]);
      }
      onClose();
    } catch (err) {
      setError('Hatırlatıcı kaydedilirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteReminder = async (id) => {
    if (!window.confirm('Bu hatırlatıcıyı silmek istediğinizden emin misiniz?')) {
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      await deleteReminder(id);
      setReminders(prev => prev.filter(r => r.id !== id));
    } catch (err) {
      setError('Hatırlatıcı silinirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteReminder = async (id) => {
    setLoading(true);
    setError('');
    
    try {
      const response = await completeReminder(id);
      setReminders(prev => 
        prev.map(r => r.id === id ? response.reminder : r)
      );
    } catch (err) {
      setError('Hatırlatıcı tamamlandı olarak işaretlenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setNewReminder(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="h-full flex flex-col bg-white dark:bg-dark-300">
      {/* Header */}
      <div className="bg-gray-50 dark:bg-dark-300 border-b border-gray-200 dark:border-gray-600 p-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Hatırlatıcı Yönetimi</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">Domain, hosting ve diğer hizmetlerinizin sürelerini takip edin</p>
          </div>
          <Button
            color="primary"
            startContent={<FiPlus />}
            onClick={handleAddReminder}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            Yeni Hatırlatıcı
          </Button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mx-6 mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-center">
            <FiAlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 mr-2" />
            <span className="text-red-800 dark:text-red-200">{error}</span>
          </div>
        </div>
      )}

      {/* Reminders List */}
      <div className="flex-1 overflow-y-auto bg-white dark:bg-dark-300 mt-2">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <Spinner size="lg" />
              <p className="text-gray-600 dark:text-gray-400 mt-4">Hatırlatıcılar yükleniyor...</p>
            </div>
          </div>
        ) : reminders.length > 0 ? (
          <div className="min-w-full px-6">
            {/* Table Header */}
            <div className="bg-gray-50 dark:bg-dark-300 border-b border-gray-200 dark:border-gray-600 sticky top-0 z-10">
              <div className="grid grid-cols-12 gap-2 py-3 text-xs font-medium text-gray-700 dark:text-gray-300">
                <div className="col-span-1 pl-2">#</div>
                <div className="col-span-2">Tür</div>
                <div className="col-span-3">Ad</div>
                <div className="col-span-2">Açıklama</div>
                <div className="col-span-2">Bitiş Tarihi</div>
                <div className="col-span-1">Durum</div>
                <div className="col-span-1">İşlemler</div>
              </div>
            </div>

            {/* Table Body */}
            <div className="divide-y divide-gray-200 dark:divide-gray-600">
              {reminders.map((reminder, index) => {
                const typeInfo = getReminderType(reminder.type);
                const statusInfo = getStatusInfo(reminder);
                const IconComponent = typeInfo.icon;

                return (
                  <div
                    key={reminder.id}
                    className={`grid grid-cols-12 gap-2 py-4 hover:bg-gray-50 dark:hover:bg-gray-600/50 transition-colors ${
                      statusInfo.status === 'expired' ? 'bg-red-50/50 dark:bg-red-900/10' : 
                      statusInfo.status === 'warning' ? 'bg-orange-50/50 dark:bg-orange-900/10' : 
                      'bg-white dark:bg-dark-300'
                    }`}
                  >
                    <div className="col-span-1 flex items-center">
                      <span className="text-sm text-gray-600 dark:text-gray-400 pl-2">{index + 1}</span>
                    </div>
                    
                    <div className="col-span-2 flex items-center">
                      <div className="flex items-center space-x-2">
                        <IconComponent className={`w-4 h-4 ${typeInfo.color}`} />
                        <span className="text-sm text-gray-700 dark:text-gray-300">{typeInfo.label}</span>
                      </div>
                    </div>
                    
                    <div className="col-span-3 flex items-center">
                      <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {reminder.name}
                      </span>
                    </div>
                    
                    <div className="col-span-2 flex items-center">
                      <span className="text-sm text-gray-600 dark:text-gray-300 truncate">
                        {reminder.description}
                      </span>
                    </div>
                    
                    <div className="col-span-2 flex items-center">
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        {format(new Date(reminder.endDate), 'dd.MM.yyyy', { locale: tr })}
                      </span>
                    </div>
                    
                    <div className="col-span-1 flex items-center justify-between">
                      <div className={`px-2 py-1 rounded-full text-xs font-medium ${statusInfo.bgColor} ${statusInfo.color} ${statusInfo.borderColor} border`}>
                        {statusInfo.text}
                      </div>
                    </div>
                    
                    <div className="col-span-1 flex items-center justify-center space-x-1">
                      {reminder.status !== REMINDER_STATUS.COMPLETED && (
                        <button
                          onClick={() => handleCompleteReminder(reminder.id)}
                          className="p-1 text-gray-400 hover:text-green-600 dark:hover:text-green-400 transition-colors"
                          title="Tamamlandı olarak işaretle"
                        >
                          <FiCheck size={14} />
                        </button>
                      )}
                      <button
                        onClick={() => handleEditReminder(reminder)}
                        className="p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                        title="Düzenle"
                      >
                        <FiEdit2 size={14} />
                      </button>
                      <button
                        onClick={() => handleDeleteReminder(reminder.id)}
                        className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                        title="Sil"
                      >
                        <FiTrash2 size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-center p-6">
            <FiCalendar className="w-16 h-16 text-gray-300 dark:text-gray-600 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Henüz hatırlatıcı yok</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">İlk hatırlatıcınızı ekleyerek başlayın</p>
            <Button
              color="primary"
              startContent={<FiPlus />}
              onClick={handleAddReminder}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Hatırlatıcı Ekle
            </Button>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalContent>
          <ModalHeader>
            {editingReminder ? 'Hatırlatıcı Düzenle' : 'Yeni Hatırlatıcı'}
          </ModalHeader>
          <ModalBody>
            <div className="space-y-4">
              <Input
                label="Hatırlatıcı Adı"
                placeholder="Örn: heypena.com Domain"
                value={newReminder.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                classNames={{
                  inputWrapper: "bg-white dark:bg-dark-300 border-gray-300 dark:border-gray-600",
                  input: "text-gray-900 dark:text-white",
                  label: "text-gray-700 dark:text-gray-300"
                }}
              />
              
              <Textarea
                label="Açıklama"
                placeholder="Hatırlatıcı hakkında detaylı bilgi"
                value={newReminder.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                classNames={{
                  inputWrapper: "bg-white dark:bg-dark-300 border-gray-300 dark:border-gray-600",
                  input: "text-gray-900 dark:text-white",
                  label: "text-gray-700 dark:text-gray-300"
                }}
              />
              
              <Select
                label="Tür"
                placeholder="Hatırlatıcı türünü seçin"
                selectedKeys={[newReminder.type]}
                onSelectionChange={(keys) => handleInputChange('type', Array.from(keys)[0])}
                classNames={{
                  trigger: "bg-white dark:bg-dark-300 border-gray-300 dark:border-gray-600",
                  value: "text-gray-900 dark:text-white",
                  label: "text-gray-700 dark:text-gray-300"
                }}
              >
                {reminderTypes.map((type) => (
                  <SelectItem key={type.key} value={type.key}>
                    <div className="flex items-center space-x-2">
                      <type.icon className={`w-4 h-4 ${type.color}`} />
                      <span>{type.label}</span>
                    </div>
                  </SelectItem>
                ))}
              </Select>
              
              <div className="grid grid-cols-2 gap-4">
                <Input
                  type="date"
                  label="Bitiş Tarihi"
                  value={newReminder.endDate}
                  onChange={(e) => handleInputChange('endDate', e.target.value)}
                  classNames={{
                    inputWrapper: "bg-white dark:bg-dark-300 border-gray-300 dark:border-gray-600",
                    input: "text-gray-900 dark:text-white",
                    label: "text-gray-700 dark:text-gray-300"
                  }}
                />
                
                <Input
                  type="number"
                  label="Hatırlatma Günü"
                  placeholder="30"
                  value={newReminder.reminderDays}
                  onChange={(e) => handleInputChange('reminderDays', parseInt(e.target.value) || 30)}
                  classNames={{
                    inputWrapper: "bg-white dark:bg-dark-300 border-gray-300 dark:border-gray-600",
                    input: "text-gray-900 dark:text-white",
                    label: "text-gray-700 dark:text-gray-300"
                  }}
                />
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="danger" variant="light" onPress={onClose} isDisabled={loading}>
              İptal
            </Button>
            <Button 
              color="primary" 
              onPress={handleSaveReminder}
              className="bg-red-600 hover:bg-red-700 text-white"
              isLoading={loading}
              isDisabled={loading}
            >
              {editingReminder ? 'Güncelle' : 'Ekle'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default RemindersComponent;
